// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatGridListModule } from '@angular/material/grid-list';
import { Analysis } from "../../../../interfaces/analysis";
import { StatsSvChromosomeColumns } from '../../../../shared/constants/hce/hce-stats-constants';
import { MessageModalService } from '../../../../services/message-modal/message-modal.service';
import { HceStatsService } from '../../../../services/hce/hce-stats/hce-stats.service';
import { HceStatsTableComponent, TableRecords } from '../stats-table/hce-stats-table.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NgIf } from '@angular/common';
import { RefsNameMapping } from '../../../../interfaces/hce/hce-stats';

@Component({
  selector: 'app-hce-stats-view-assembly',
  standalone: true,
  imports: [
    HceStatsTableComponent,
    MatGridListModule,
    MatProgressSpinnerModule,
    NgIf,
],
  templateUrl: './hce-stats-view-assembly.component.html',
  styleUrl: './hce-stats-view-assembly.component.css',
})
export class HceStatsViewAssemblyComponent {
  @Input() analysis!: Analysis | null;
  @Input() refNameMap?: RefsNameMapping;
  @Output() cellFilterRequest = new EventEmitter<{
    chr?: string | number;
  }>();

  constructor(
    private statsService: HceStatsService,
    private messageModal: MessageModalService
  ) {}

  /*
   * Genome List
   */
  cenomeDisplayedColumnLabels: string[] = [
    'Item',
    'Statistic',
  ];
  cenomeDisplayedColumns: string[] = [
    'key',
    'value',
  ];
  genomeListData?: TableRecords = [];

  /*
   * Chromosome List
   */
  chromosomeDisplayedColumnLabels: string[] = [
    'CHR',
    'Allele Frequency',
    'Average Length (Mb)',
    'Covered Length (bp)',
    'Covered Length Ratio (%)',
    'Covered Sites Ratio (%)',
    'Cuts Coverage Average',
    'Cuts Depth Average',
    'Ktuple Coverage Average',
    'Max Length (Mb)',
    'Median Length (Mb)',
    'Min Length (Mb)',
    'Number Of Contigs',
    'Reference Length (bp)',
    'Total Length (Mb)',
  ];
  chromosomeDisplayedColumns: string[] = [
    StatsSvChromosomeColumns.CHR,
    StatsSvChromosomeColumns.COL1,
    StatsSvChromosomeColumns.COL2,
    StatsSvChromosomeColumns.COL3,
    StatsSvChromosomeColumns.COL4,
    StatsSvChromosomeColumns.COL5,
    StatsSvChromosomeColumns.COL6,
    StatsSvChromosomeColumns.COL7,
    StatsSvChromosomeColumns.COL8,
    StatsSvChromosomeColumns.COL9,
    StatsSvChromosomeColumns.COL10,
    StatsSvChromosomeColumns.COL11,
    StatsSvChromosomeColumns.COL12,
    StatsSvChromosomeColumns.COL13,
    StatsSvChromosomeColumns.COL14,
  ];
  chromosomeListData?: TableRecords = [];

  isLoading = true;
  hasError = false;

  ngOnInit() {
  }

  ngOnChanges() {
    const hasAnalysis = !!this.analysis?.AnalysisID;
    const hasRefNameMap =
      this.refNameMap &&
      Object.keys(this.refNameMap).length > 0;

    if (hasAnalysis && hasRefNameMap) {
      this.isLoading = true;
      this.loadStatsCoverage();
    }
  }

  private showLoadFailed(): void {
    this.messageModal.openModal(
      $localize`Error`,
      $localize`Failed to load assembly statistics.`
    );
  }

  private loadStatsCoverage() {
    if (!this.analysis || !this.refNameMap) {
      return;
    }

    const analysisId = this.analysis.AnalysisID;
    this.statsService
      .getStatsCoverage(analysisId, this.refNameMap)
      .subscribe({
        next: (response) => {
          if (response?.success) {
            this.genomeListData = response.assembleGenome;
            this.chromosomeListData = response.assembleChromosome;
          } else {
            if (Array.isArray(response?.errorMessage) && response.errorMessage.length > 0) {
              console.error(response.errorMessage.join(', '));
            }
            this.showLoadFailed();
            this.hasError = true;
          }
          this.isLoading = false;
        },
        error: (err) => {
          console.error('get Stats Coverage failed:', err);
          this.showLoadFailed();
          this.isLoading = false;
          this.hasError = true;
        },
      });
  }

  onCellFilterRequest(e: { chr?: string | number }) {
    this.cellFilterRequest.emit({ chr: e.chr });
  }

}
